import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const TABS = [
  { id: 'pending', label: 'Pendentes' },
  { id: 'approved', label: 'Aprovados' },
  { id: 'suspended', label: 'Suspensos' },
  { id: 'all', label: 'Todos' },
];

export function AdminBusinessesPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const fetchList = useCallback(async (status, options = {}) => {
    setLoading(true);
    // Recarga após uma ação (ex.: aprovação) preserva a mensagem de sucesso;
    // apenas troca de aba ou erro limpa a mensagem anterior.
    if (!options.keepMessage) setMessage(null);
    try {
      const { data: res } = await api.get('/admin/businesses', { params: { status, limit: 50 } });
      setData(res.data || []);
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao carregar comércios' });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(tab); }, [tab, fetchList]);

  const act = async (action, id, name) => {
    setMessage(null);
    try {
      if (action === 'approve') {
        await api.put(`/admin/businesses/${id}/approve`);
        setMessage({ type: 'ok', text: `"${name}" aprovado!` });
      } else if (action === 'suspend') {
        await api.put(`/admin/businesses/${id}/suspend`);
        setMessage({ type: 'ok', text: `"${name}" suspenso.` });
      } else if (action === 'reactivate') {
        await api.put(`/admin/businesses/${id}/reactivate`);
        setMessage({ type: 'ok', text: `"${name}" reativado.` });
      }
      fetchList(tab, { keepMessage: true });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro na operação.' });
    }
  };

  const categoryLabel = (c) => ({ mercado: 'Mercado', farmacia: 'Farmácia', padaria: 'Padaria', restaurante: 'Restaurante', servicos: 'Serviços', evento: 'Evento', publico: 'Público', mercado_publico: 'Mercado Público' }[c] || c);

  return (
    <div className="nc-page">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-primary) 0%, var(--nc-primary-dark) 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 18px', borderRadius: '0 0 22px 22px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate('/admin')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Comércios</h1>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                background: tab === t.id ? '#fff' : 'rgba(255,255,255,0.15)',
                color: tab === t.id ? '#0d5c3b' : '#fff', fontSize: 12, fontWeight: 500
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {loading ? <Loading /> : data.length === 0 ? (
          <EmptyState
            icon="📭"
            title="Nada por aqui"
            description={tab === 'pending' ? 'Nenhum comércio aguardando aprovação.' : 'Nenhum comércio nesta categoria.'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.map(b => (
              <div key={b.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10, background: 'var(--nc-surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0
                  }}>🏪</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>{b.name}</div>
                    <div className="nc-hint" style={{ marginTop: 2 }}>
                      {categoryLabel(b.category)} • {b.city_name}/{b.state}
                    </div>
                    {b.address && <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginTop: 2 }}>{b.address}</div>}
                    {b.owner_name && <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginTop: 2 }}>Solicitante: {b.owner_name}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {!b.is_approved && b.is_active && (
                    <button onClick={() => act('approve', b.id, b.name)} className="nc-btn-primary" style={{ flex: 1, padding: '9px 0', fontSize: 13 }}>
                      ✓ Aprovar
                    </button>
                  )}
                  {b.is_active ? (
                    <button onClick={() => act('suspend', b.id, b.name)} style={{
                      flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-danger-border)',
                      background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', fontSize: 13, fontWeight: 500, cursor: 'pointer'
                    }}>
                      Suspender
                    </button>
                  ) : (
                    <button onClick={() => act('reactivate', b.id, b.name)} className="nc-btn-outline" style={{ flex: 1, padding: '9px 0', fontSize: 13 }}>
                      Reativar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
